/** 信用卡拍照辅助录入：拍照或选相册后识别四项卡面信息，用户核对后仍用原表单保存。
 * 原图仅用于本次识别，不走附件上传。失败保留本次文件以便重试，关闭弹窗取消请求。
 */
import React, { useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, Loader2, RotateCcw } from "lucide-react";
import { Button } from "../../components/ui/button";

/** api 沿用当前登录会话；onBusy 锁住父表单，onRecognized 只接收四项可核对建议。 */
export default function CreditCardPhotoImport({ zh, api, disabled, onBusy, onRecognized }) {
  const camera = useRef(null), picker = useRef(null), controller = useRef(null);
  const [file, setFile] = useState(null), [busy, setBusy] = useState(false);
  const [error, setError] = useState(""), [done, setDone] = useState(false);
  useEffect(() => () => controller.current?.abort(), []);

  /** 识别期间禁用重复操作；取消/卸载后不回填迟到结果，失败也不清空已有表单。 */
  const scan = async (photo) => {
    if (disabled || controller.current || !photo) return;
    const request = new AbortController(); controller.current = request;
    setBusy(true); onBusy(true); setError(""); setDone(false);
    try {
      const body = new FormData(); body.append("file", photo);
      const { data } = await api.post("/finance/credit-cards/recognize", body, { signal: request.signal, timeout: 75000 });
      if (request.signal.aborted) return;
      onRecognized(data.fields || {}); setDone(true); setFile(null);
    } catch (e) {
      if (!request.signal.aborted) setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail :
        (zh ? "识别失败，请重试、换张照片或手动填写。" : "Recognition failed. Retry, choose another photo, or enter details manually."));
    } finally {
      if (!request.signal.aborted) { controller.current = null; setBusy(false); onBusy(false); }
    }
  };

  /** 复用原生拍照/相册入口；清空 input 允许再次选择同一照片，大小和格式同时做前后端校验。 */
  const choose = (event) => {
    const photo = event.target.files?.[0]; event.target.value = "";
    if (!photo || disabled || controller.current) return;
    setFile(null); setDone(false);
    if (!/\.(jpe?g|png|webp|heic|heif)$/i.test(photo.name) || !photo.size || photo.size > 10 * 1024 * 1024) {
      setError(zh ? "请选择 10 MB 以内的 JPG、PNG、WEBP 或 HEIC 照片。" : "Choose a JPG, PNG, WEBP or HEIC photo up to 10 MB."); return;
    }
    setFile(photo); scan(photo);
  };

  return <section className="rounded-xl border border-border bg-muted/50 p-3 space-y-2" aria-busy={busy} data-testid="cc-photo-import">
    <div className="flex flex-wrap gap-2">
      <Button type="button" variant="outline" size="sm" disabled={disabled || busy} data-testid="cc-photo-camera-btn" onClick={() => camera.current?.click()}>
        <Camera className="h-4 w-4 mr-1.5" aria-hidden="true" />{zh ? "拍照识别" : "Scan card"}
      </Button>
      <Button type="button" variant="outline" size="sm" disabled={disabled || busy} data-testid="cc-photo-pick-btn" onClick={() => picker.current?.click()}>
        <ImagePlus className="h-4 w-4 mr-1.5" aria-hidden="true" />{zh ? "上传照片" : "Upload photo"}
      </Button>
    </div>
    <input ref={camera} type="file" accept="image/*" capture="environment" className="hidden" onChange={choose} data-testid="cc-photo-camera" />
    <input ref={picker} type="file" accept=".jpg,.jpeg,.png,.webp,.heic,.heif" className="hidden" onChange={choose} data-testid="cc-photo-file" />
    <p className="text-xs text-muted-foreground">{zh ? "识别卡名称、发卡行、末四位及持卡人，仅补充空白项。照片用于 AI 识别，不保存为附件。" : "Read card name, bank, last 4 digits and holder into empty fields. Photos are sent for AI recognition, not saved as attachments."}</p>
    {busy && <p role="status" className="flex items-center gap-1.5 text-sm text-foreground"><Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />{zh ? "正在识别，请稍候…" : "Reading card…"}</p>}
    {done && <p role="status" className="text-sm text-foreground" data-testid="cc-photo-success">{zh ? "识别完成，请核对信息并补充空白项后保存。" : "Photo read. Review the details and complete missing fields before saving."}</p>}
    {error && <div className="space-y-2"><p role="alert" className="text-sm text-destructive" data-testid="cc-photo-error">{error}</p>
      {file && <Button type="button" variant="outline" size="sm" disabled={disabled || busy} onClick={() => scan(file)} data-testid="cc-photo-retry"><RotateCcw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />{zh ? "重新识别" : "Retry"}</Button>}
    </div>}
  </section>;
}
